import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import axios from "axios";
import { FetchApi } from "../../utils/api";

// Interfaces
interface BookingParameters {
  [key: string]: any;
}

interface PaymentStatusResponse {
  [key: string]: any;
}

interface PromotionEligibilityParams {
  ordertype: string; // room, voucher, product
  promotion_code: string;
  amount: number;
}

interface PromotionEligibilityResponse {
  code: string;
  expirydate: string;
  startdate: string;
  type: string;
  eligible: string;
  description: string;
  discount_amount: string;
}

interface CheckoutContextType {
  loading: boolean;
  error: string | null;
  checkPaymentStatus: (
    parameters: BookingParameters
  ) => Promise<PaymentStatusResponse>;
  checkPromotionEligibility: (
    parameters: PromotionEligibilityParams
  ) => Promise<PromotionEligibilityResponse>;
}

const CheckoutContext = createContext<CheckoutContextType | undefined>(
  undefined
);

export const CheckoutProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkPaymentStatus = useCallback(
    async (parameters: BookingParameters): Promise<PaymentStatusResponse> => {
      setLoading(true);
      setError(null);
      try {
        const url = FetchApi("checkstatus.php", parameters);
        console.log("check url checkPaymentStatus ===> ", url);
        const response = await axios.get(url);
        console.log("check response checkPaymentStatus ===> ", response.data);
        return response.data;
      } catch (err: any) {
        console.log("Catch Error ======= checkPaymentStatus", err);
        const errorMsg = err?.message || "Failed to check payment status";
        setError(errorMsg);
        return Promise.reject(err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const checkPromotionEligibility = useCallback(
    async (
      parameters: PromotionEligibilityParams
    ): Promise<PromotionEligibilityResponse> => {
      setLoading(true);
      setError(null);
      try {
        // Add required API credentials
        const params = {
          ...parameters,
        };

        const url = FetchApi("promotion_code_eligible.php", params);
        console.log("check url checkPromotionEligibility ===> ", url);
        const response = await axios.get(url);
        console.log(
          "check response checkPromotionEligibility ===> ",
          response.data
        );

        // Handle the response - check if eligible
        if (response.data.eligible === "YES") {
          return response.data;
        } else {
          throw new Error(
            response.data.description || "Promo code is not eligible"
          );
        }
      } catch (err: any) {
        console.log("Catch Error ======= checkPromotionEligibility", err);
        const errorMsg =
          err?.response?.data?.description ||
          err?.message ||
          "Failed to check promotion eligibility";
        setError(errorMsg);
        return Promise.reject(new Error(errorMsg));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return (
    <CheckoutContext.Provider
      value={{
        loading,
        error,
        checkPaymentStatus,
        checkPromotionEligibility,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error("useCheckout must be used within CheckoutProvider");
  }
  return context;
};
